import React, { useState } from 'react'

export default function ContactForm({ selectedMenu, onResetMenu, title = "Forespørgsel på mad", subtitle = "Udfyld formularen, så vender vi hurtigt tilbage.", compact = false, isHeroWatermark = false }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    event_date: '',
    guest_count: '',
    event_address: '',
    message: '',
    website_url: '', // Honeypot field (must be named exactly website_url)
  })

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  })

  const [errors, setErrors] = useState({})

  const validate = () => {
    const errs = {}
    if (!formData.name.trim()) errs.name = 'Indtast venligst dit fulde navn'
    if (!formData.phone.trim()) errs.phone = 'Indtast venligst dit telefonnummer'
    if (!formData.email.trim()) {
      errs.email = 'Indtast venligst din e-mailadresse'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Indtast venligst en gyldig e-mailadresse'
    }
    if (!formData.message.trim() && !selectedMenu) {
      errs.message = 'Skriv venligst en kort besked eller information om dit arrangement'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setStatus({ submitting: true, success: false, error: null })

    try {
      // Build submission payload
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        event_date: formData.event_date || 'Ikke angivet',
        guest_count: formData.guest_count || 'Ikke angivet',
        event_address: formData.event_address || 'Ikke angivet',
        message: formData.message.trim() || 'Menu sammensat via online bestillingsguide.',
        website_url: formData.website_url, // Honeypot
      }

      // If user came through the menu guide, attach full menu breakdown
      if (selectedMenu) {
        payload.menu_forretter = (selectedMenu.starters || []).map((s) => s.title).join(', ') || 'Ingen'
        payload.menu_koed = (selectedMenu.meats || []).map((m) => m.title).join(', ') || 'Ingen'
        payload.menu_kartofler = (selectedMenu.potatoes || []).map((p) => p.title).join(', ') || 'Ingen'
        payload.menu_salater = (selectedMenu.salads || []).map((s) => s.title).join(', ') || 'Ingen'
        if (selectedMenu.pricePerGuest) {
          payload.kuvertpris = `${selectedMenu.pricePerGuest} kr.`
        }
        if (selectedMenu.totalPrice) {
          payload.samlet_beregnet_pris = `${selectedMenu.totalPrice} kr.`
        }
        payload.samlet_menu_tekst = `Sammensat menu:
- Forret: ${payload.menu_forretter}
- Kød: ${payload.menu_koed}
- Kartofler: ${payload.menu_kartofler}
- Salater: ${payload.menu_salater}
${selectedMenu.pricePerGuest ? `- Beregnet kuvertpris: ${selectedMenu.pricePerGuest} kr. pr. kuvert` : ''}
${selectedMenu.totalPrice && formData.guest_count ? `- Samlet estimeret pris: ${selectedMenu.totalPrice} kr. (for ${formData.guest_count} kuverter)` : ''}`
      }

      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        throw new Error('Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring til os.')
      }

      setStatus({ submitting: false, success: true, error: null })
      if (onResetMenu) onResetMenu()
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der opstod en fejl. Prøv venligst igen eller ring på 25 75 27 24.',
      })
    }
  }

  return (
    <div className={`p-6 sm:p-8 transition-shadow duration-300 hover:shadow-xl ${
      isHeroWatermark
        ? 'bg-white watermark-bg'
        : 'bg-white shadow-xl'
    }`}>
      {status.success ? (
        <div className="text-center py-10 space-y-4">
          <div className="w-16 h-16 bg-[#F3EFEA] text-[#C25E27] rounded-full flex items-center justify-center mx-auto text-3xl">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h3 className="text-2xl font-bold text-[#241F1C]">Tak for din henvendelse!</h3>
          <p className="text-[#6E635C] max-w-md mx-auto leading-relaxed">
            Vi har modtaget din forespørgsel og vender tilbage til dig hurtigst muligt med en uforpligtende snak og bekræftelse på dit arrangement.
          </p>
          <div className="pt-4">
            <button
              type="button"
              onClick={() => {
                setStatus({ submitting: false, success: false, error: null })
                setFormData({
                  name: '',
                  phone: '',
                  email: '',
                  event_date: '',
                  guest_count: '',
                  event_address: '',
                  message: '',
                  website_url: '',
                })
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#FAF8F5] text-[#241F1C] border border-[#E6DFD5] hover:bg-[#F3EFEA] font-medium text-sm transition-colors cursor-pointer"
            >
              <span>Send en ny besked</span>
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* Honeypot field - mandatory anti-spam requirement */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor="website_url">Lad dette felt være tomt</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* If the user has a selected menu from the guide, display summary box */}
          {selectedMenu && (
            <div className="p-4 sm:p-5 rounded-xl bg-white border border-[#E6DFD5] space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-[#E6DFD5]">
                <span className="text-xs uppercase font-bold tracking-wider text-[#C25E27]">
                  Tilknyttede menuvalg fra bestillingsguiden
                </span>
                {selectedMenu.pricePerGuest > 0 && (
                  <span className="text-xs font-bold text-[#241F1C] bg-[#FAF8F5] px-2.5 py-1 rounded-md border border-[#E6DFD5]">
                    Samlet kuvertpris: <span className="text-[#C25E27]">{selectedMenu.pricePerGuest} kr.</span>
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-[#241F1C]">
                {selectedMenu.starters?.length > 0 && (
                  <div>
                    <span className="font-semibold text-[#6E635C]">Forret: </span>
                    {selectedMenu.starters.map((s) => s.title).join(', ')}
                  </div>
                )}
                {selectedMenu.meats?.length > 0 && (
                  <div>
                    <span className="font-semibold text-[#6E635C]">Kød: </span>
                    {selectedMenu.meats.map((m) => m.title).join(', ')}
                  </div>
                )}
                {selectedMenu.potatoes?.length > 0 && (
                  <div>
                    <span className="font-semibold text-[#6E635C]">Kartofler: </span>
                    {selectedMenu.potatoes.map((p) => p.title).join(', ')}
                  </div>
                )}
                {selectedMenu.salads?.length > 0 && (
                  <div>
                    <span className="font-semibold text-[#6E635C]">Salater: </span>
                    {selectedMenu.salads.map((s) => s.title).join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Navn */}
            <div>
              <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-[#241F1C] mb-1">
                Dit navn <span className="text-[#C25E27]">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="F.eks. Jens Jensen"
                className={`w-full py-2.5 px-1 bg-transparent border-0 border-b text-sm transition-all focus:outline-none focus:border-[#C25E27] placeholder:text-[#8C827A] placeholder:opacity-100 ${
                  errors.name ? 'border-red-500' : 'border-[#241F1C]/25 hover:border-[#241F1C]/50'
                }`}
                required
              />
              {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Telefonnummer */}
            <div>
              <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-[#241F1C] mb-1">
                Telefonnummer <span className="text-[#C25E27]">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 25 75 27 24"
                className={`w-full py-2.5 px-1 bg-transparent border-0 border-b text-sm transition-all focus:outline-none focus:border-[#C25E27] placeholder:text-[#8C827A] placeholder:opacity-100 ${
                  errors.phone ? 'border-red-500' : 'border-[#241F1C]/25 hover:border-[#241F1C]/50'
                }`}
                required
              />
              {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* E-mail (must be named exactly 'email' for notifications) */}
            <div>
              <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-[#241F1C] mb-1">
                E-mailadresse <span className="text-[#C25E27]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="F.eks. navn@mail.dk"
                className={`w-full py-2.5 px-1 bg-transparent border-0 border-b text-sm transition-all focus:outline-none focus:border-[#C25E27] placeholder:text-[#8C827A] placeholder:opacity-100 ${
                  errors.email ? 'border-red-500' : 'border-[#241F1C]/25 hover:border-[#241F1C]/50'
                }`}
                required
              />
              {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Dato for fest/arrangement */}
            <div>
              <label htmlFor="event_date" className="block text-xs font-bold uppercase tracking-wider text-[#241F1C] mb-1">
                Dato for arrangement
              </label>
              <input
                type="date"
                id="event_date"
                name="event_date"
                value={formData.event_date}
                onChange={handleChange}
                placeholder="Vælg dato for festen"
                className="w-full py-2.5 px-1 bg-transparent border-0 border-b border-[#241F1C]/25 hover:border-[#241F1C]/50 text-sm focus:outline-none focus:border-[#C25E27] placeholder:text-[#8C827A] placeholder:opacity-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Antal kuverter/gæster */}
            <div>
              <label htmlFor="guest_count" className="block text-xs font-bold uppercase tracking-wider text-[#241F1C] mb-1">
                Antal gæster / kuverter
              </label>
              <input
                type="number"
                id="guest_count"
                name="guest_count"
                min="10"
                max="500"
                value={formData.guest_count}
                onChange={handleChange}
                placeholder="F.eks. 45 personer"
                className="w-full py-2.5 px-1 bg-transparent border-0 border-b border-[#241F1C]/25 hover:border-[#241F1C]/50 text-sm focus:outline-none focus:border-[#C25E27] placeholder:text-[#8C827A] placeholder:opacity-100"
              />
            </div>

            {/* Leveringsadresse / By */}
            <div>
              <label htmlFor="event_address" className="block text-xs font-bold uppercase tracking-wider text-[#241F1C] mb-1">
                Festadresse / By
              </label>
              <input
                type="text"
                id="event_address"
                name="event_address"
                value={formData.event_address}
                onChange={handleChange}
                placeholder="F.eks. Simmelbrovej 52, 7260 Sønder Omme"
                className="w-full py-2.5 px-1 bg-transparent border-0 border-b border-[#241F1C]/25 hover:border-[#241F1C]/50 text-sm focus:outline-none focus:border-[#C25E27] placeholder:text-[#8C827A] placeholder:opacity-100"
              />
            </div>
          </div>

          {/* Beskrivelse / yderligere ønsker */}
          <div>
            <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-[#241F1C] mb-1">
              Beskrivelse / bemærkninger
            </label>
            <textarea
              id="message"
              name="message"
              rows={compact ? 2 : 3}
              value={formData.message}
              onChange={handleChange}
              placeholder="Dine ønsker eller spørgsmål..."
              className={`w-full py-2 px-1 bg-transparent border-0 border-b text-sm transition-all focus:outline-none focus:border-[#C25E27] resize-none placeholder:text-[#8C827A] placeholder:opacity-100 ${
                errors.message ? 'border-red-500' : 'border-[#241F1C]/25 hover:border-[#241F1C]/50'
              }`}
            />
            {errors.message && <p className="text-red-600 text-xs mt-1">{errors.message}</p>}
          </div>

          {status.error && (
            <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
              <i className="fa-solid fa-triangle-exclamation mr-2" aria-hidden="true" />
              {status.error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={status.submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] disabled:bg-[#6E635C] text-white px-5 py-2.5 sm:px-8 sm:py-3.5 font-bold text-xs sm:text-sm transition-shadow duration-300 hover:shadow-lg cursor-pointer cta-hop"
            >
              {status.submitting ? (
                <>
                  <i className="fa-solid fa-spinner animate-spin" aria-hidden="true" />
                  <span>Sender forespørgsel...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                  <span>Send uforpligtende forespørgsel</span>
                </>
              )}
            </button>
            <p className="text-xs text-[#6E635C] mt-2">
              Vi vender tilbage hurtigst muligt. Du binder dig ikke til noget ved at sende denne henvendelse.
            </p>
          </div>
        </form>
      )}
    </div>
  )
}
